/**
 * The characters a folder name may hold, mirroring `validateFolderPath` in the API's
 * `user-filesystem-utils`. A name becomes one segment of the folder's key, so anything outside this
 * set has to be kept out of the name — "/" above all, since the key joins the parent path and the
 * name together and a separator inside the name reads as another level of folders.
 *
 * The whitespace here is a literal space, not `\s`: tab, line breaks, non-breaking spaces and the
 * byte-order mark leave no mark on screen, so a folder holding one looks exactly like a folder
 * without it — and the two sit side by side in the tree looking like the same folder twice.
 */
const INVALID_FOLDER_NAME_CHARACTER = /[^A-Za-z0-9-_ ~`\[\]!@#$%^&()=,;.]/g;

/** What to call a character with no visible form, so the dialog can say what is wrong. */
const INVISIBLE_CHARACTER_NAMES: Record<string, string> = {
  '\t': 'a tab',
  '\n': 'a line break',
  '\r': 'a line break',
  '\v': 'a vertical tab',
  '\f': 'a form feed',
  '\u00a0': 'a non-breaking space',
  '\u2028': 'a line separator',
  '\u2029': 'a paragraph separator',
  '\u3000': 'a full-width space',
  '\ufeff': 'a zero-width space',
};

const describe = (character: string) =>
  INVISIBLE_CHARACTER_NAMES[character] ?? (/\s/.test(character) ? 'an invisible space' : character);

/**
 * The characters of a folder name the API would reject, each listed once, in the order typed.
 * A character that cannot be seen is named rather than shown.
 */
export const getInvalidFolderNameCharacters = (name: string): string[] =>
  Array.from(new Set((name.match(INVALID_FOLDER_NAME_CHARACTER) || []).map(describe)));

/**
 * The key of the folder one level up from `fullKey`, which is what CENTERLINE Drive's single panel
 * layout walks back through: it has no tree to click a parent in, only an up arrow.
 *
 * Keys are absolute and folder keys end in "/", so this trims the last segment: "/A/B/" gives
 * "/A/", "/A/" gives the root "/", and the root has nothing above it. A file's key (no trailing
 * slash) gives the folder holding it.
 */
export const getParentFolderKey = (fullKey: string): string | undefined => {
  if (!fullKey || fullKey === '/') return undefined;
  return fullKey.replace(/[^/]+\/?$/, '');
};
